import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  User, Building2, Mail, Phone, Lock, MapPin, ArrowRight, 
  CheckCircle, AlertCircle, Eye, EyeOff, Briefcase, Store
} from 'lucide-react';
import { toast } from 'sonner';

const ACCOUNT_TYPES = [
  {
    id: 'particulier',
    title: 'Particulier',
    subtitle: 'Je veux acheter ou louer du matériel',
    icon: User,
    color: 'from-blue-500 to-blue-600',
    features: [
      'Accès au catalogue complet',
      'Demande de devis',
      'Sauvegarde de favoris',
      'Historique des demandes'
    ]
  },
  {
    id: 'professionnel',
    title: 'Professionnel',
    subtitle: 'Je veux vendre ou louer mon matériel',
    icon: Briefcase,
    color: 'from-[#635BFF] to-[#8B85FF]',
    popular: true,
    features: [
      'Publier jusqu\'à 5 annonces gratuitement',
      'Gérer mes annonces',
      'Statistiques de vues',
      'Contact direct acheteurs'
    ]
  },
  {
    id: 'entreprise',
    title: 'Entreprise / Loueur',
    subtitle: 'Je gère un parc de matériels',
    icon: Building2,
    color: 'from-amber-500 to-amber-600',
    features: [
      'Boutique dédiée',
      'Import CSV de catalogue',
      'API disponible (Premium)',
      'Support prioritaire'
    ]
  }
];

export default function Inscription() {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Choix type, 2: Formulaire
  const [selectedType, setSelectedType] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    telephone: '',
    entreprise: '',
    siret: '',
    ville: '',
    acceptCGU: false,
    acceptNewsletter: false
  });

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.nom || !formData.telephone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return false;
    }
    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return false;
    }
    if (!formData.acceptCGU) {
      toast.error('Veuillez accepter les conditions générales');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: `${formData.prenom} ${formData.nom}`,
        phone: formData.telephone,
        company_name: formData.entreprise,
        siret: formData.siret,
        city: formData.ville,
        account_type: selectedType,
        roles: selectedType === 'particulier' ? ['user'] : ['user', 'supplier'],
        newsletter: formData.acceptNewsletter
      });

      toast.success('Compte créé avec succès ! Vérifiez votre email.');
      
      // Auto login
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        navigate('/mon-compte');
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la création du compte';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <span className="text-2xl font-bold">
              <span className="text-[#0A2540]">GEOTS</span>
              <span className="text-[#635BFF]">.consulting</span>
            </span>
          </Link>
          <h1 className="text-3xl font-bold text-[#0A2540] mb-2">
            {step === 1 ? 'Créer un compte' : `Inscription ${ACCOUNT_TYPES.find(t => t.id === selectedType)?.title}`}
          </h1>
          <p className="text-gray-600">
            {step === 1 
              ? 'Choisissez le type de compte qui vous correspond'
              : 'Complétez vos informations pour finaliser votre inscription'
            }
          </p>
        </div>

        {/* Progress */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#635BFF] text-white' : 'bg-gray-200'}`}>1</div>
            <div className={`w-24 h-1 ${step >= 2 ? 'bg-[#635BFF]' : 'bg-gray-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#635BFF] text-white' : 'bg-gray-200'}`}>2</div>
          </div>
        </div>

        {/* Step 1: Choose Account Type */}
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ACCOUNT_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <Card 
                  key={type.id}
                  className={`cursor-pointer transition-all duration-300 hover:shadow-xl ${
                    selectedType === type.id ? 'ring-2 ring-[#635BFF]' : ''
                  } ${type.popular ? 'md:-mt-4 md:mb-4' : ''}`}
                  onClick={() => handleTypeSelect(type.id)}
                >
                  {type.popular && (
                    <div className="bg-[#635BFF] text-white text-center py-1 text-xs font-semibold">
                      ⭐ LE PLUS POPULAIRE
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${type.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A2540] mb-1">{type.title}</h3>
                    <p className="text-sm text-gray-500 mb-4">{type.subtitle}</p>
                    
                    <ul className="space-y-2 mb-6">
                      {type.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button className={`w-full bg-gradient-to-r ${type.color} hover:opacity-90`}>
                      Choisir
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Step 2: Registration Form */}
        {step === 2 && (
          <Card className="max-w-xl mx-auto">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nom / Prénom */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                    <Input
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleInputChange}
                      placeholder="Jean"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                    <Input
                      name="nom"
                      value={formData.nom}
                      onChange={handleInputChange}
                      placeholder="Dupont"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jean.dupont@exemple.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Téléphone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      placeholder="06 12 34 56 78"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Entreprise (pour pro et entreprise) */}
                {(selectedType === 'professionnel' || selectedType === 'entreprise') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entreprise {selectedType === 'entreprise' ? '*' : '(optionnel)'}
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          name="entreprise"
                          value={formData.entreprise}
                          onChange={handleInputChange}
                          placeholder="Nom de l'entreprise"
                          className="pl-10"
                          required={selectedType === 'entreprise'}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SIRET (optionnel)</label>
                      <Input
                        name="siret"
                        value={formData.siret}
                        onChange={handleInputChange}
                        placeholder="123 456 789 00012"
                      />
                    </div>
                  </>
                )}

                {/* Ville */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      name="ville"
                      value={formData.ville}
                      onChange={handleInputChange}
                      placeholder="Paris"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Mot de passe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="8 caractères minimum"
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirmer mot de passe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirmez votre mot de passe"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* CGU */}
                <div className="space-y-2 pt-4">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="acceptCGU"
                      checked={formData.acceptCGU}
                      onChange={handleInputChange}
                      className="mt-1 rounded"
                      required
                    />
                    <span className="text-sm text-gray-600">
                      J'accepte les <Link to="/cgu" className="text-[#635BFF] hover:underline">conditions générales d'utilisation</Link> et la <Link to="/confidentialite" className="text-[#635BFF] hover:underline">politique de confidentialité</Link> *
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="acceptNewsletter"
                      checked={formData.acceptNewsletter}
                      onChange={handleInputChange}
                      className="mt-1 rounded"
                    />
                    <span className="text-sm text-gray-600">
                      Je souhaite recevoir les actualités et offres de GEOTS
                    </span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setStep(1)}
                    className="flex-1"
                  >
                    Retour
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-[#635BFF] hover:bg-[#635BFF]/90"
                    disabled={loading}
                  >
                    {loading ? 'Création...' : 'Créer mon compte'}
                  </Button>
                </div>
              </form>

              {/* Déjà un compte */}
              <div className="mt-6 text-center border-t pt-6">
                <p className="text-sm text-gray-600">
                  Déjà un compte ? <Link to="/login" className="text-[#635BFF] hover:underline font-semibold">Se connecter</Link>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info box */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            💡 <strong>Vous préférez qu'on s'occupe de tout ?</strong>{' '}
            <Link to="/vendre" className="text-[#635BFF] hover:underline">
              Remplissez notre formulaire simplifié
            </Link>{' '}
            et notre équipe gère votre annonce.
          </p>
        </div>
      </div>
    </div>
  );
}
